import { useTranslation } from 'react-i18next';
import { Moon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Icon } from '@/components/ui/icon';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { type ScheduleWindow, crossesMidnight, neverOpens } from '../lib';

const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const;

/**
 * The availability-window editor, used by the create wizard's step 2 and by the
 * menu detail card that edits an existing menu.
 *
 * One component for both because they write the same JSON column through the
 * same DTO, and the two warnings below are the whole reason this screen is not
 * seven text inputs. Forking it would mean the wizard learns about
 * `neverOpens` and the edit path forgets.
 *
 * The design's day picker is a compact `M T W T F S S` strip. It stays the
 * seven short day names the repo already has translated: Arabic has no
 * single-letter day form — `الأحد` does not abbreviate to a glyph — so a strip
 * built on initials is an English-only control wearing a translated label.
 */
export function ScheduleEditor({
  schedule,
  onChange,
  disabled,
}: {
  schedule: ScheduleWindow[];
  onChange: (next: ScheduleWindow[]) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();

  const patchWindow = (index: number, patch: Partial<ScheduleWindow>) =>
    onChange(schedule.map((w, i) => (i === index ? { ...w, ...patch } : w)));

  if (schedule.length === 0) {
    return <p className="text-sm text-ink-muted">{t('catalog.menus.alwaysOpen')}</p>;
  }

  return (
    <ul className={cn('space-y-3', disabled && 'pointer-events-none opacity-50')}>
      {schedule.map((window, index) => (
        // The index is the identity: windows carry no id, and two identical
        // windows are a thing an operator can legitimately type.
        <li key={index} className="space-y-2 rounded-lg border p-3">
          <div className="flex flex-wrap gap-2">
            {DAY_KEYS.map((key, day) => {
              const on = window.days.includes(day);
              return (
                <button
                  key={key}
                  type="button"
                  disabled={disabled}
                  aria-pressed={on}
                  onClick={() =>
                    patchWindow(index, {
                      days: on
                        ? window.days.filter((d) => d !== day)
                        : [...window.days, day].sort((a, b) => a - b),
                    })
                  }
                  className={cn(
                    'rounded-lg px-2.5 py-1 text-xs transition-colors',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    on ? 'bg-accent-solid text-accent-on' : 'border text-ink-muted hover:bg-hover',
                  )}
                >
                  {t(`catalog.menus.day.${key}`)}
                </button>
              );
            })}
          </div>

          <div className="flex flex-wrap items-end gap-3">
            <Field label={t('catalog.menus.from')}>
              <Input
                type="time"
                className="w-32"
                disabled={disabled}
                value={window.from}
                onChange={(e) => patchWindow(index, { from: e.target.value })}
              />
            </Field>
            <Field label={t('catalog.menus.to')}>
              <Input
                type="time"
                className="w-32"
                disabled={disabled}
                value={window.to}
                onChange={(e) => patchWindow(index, { to: e.target.value })}
              />
            </Field>
            {crossesMidnight(window) && (
              <span className="flex items-center gap-1.5 pb-2 text-xs text-amber-600">
                <Icon as={Moon} size="sm" aria-hidden />
                {t('catalog.menus.crossesMidnight')}
              </span>
            )}
            {/* The API stores `from === to` happily and then never opens the
                window, so the warning belongs here rather than on the save. */}
            {neverOpens(window) && (
              <span className="flex items-center gap-1.5 pb-2 text-xs text-red-600 dark:text-red-400">
                <Icon as={Moon} size="sm" aria-hidden />
                {t('catalog.menus.neverOpens')}
              </span>
            )}
            <Button
              size="sm"
              variant="ghost"
              className="mb-0.5 ms-auto"
              disabled={disabled}
              onClick={() => onChange(schedule.filter((_, i) => i !== index))}
            >
              {t('common.delete')}
            </Button>
          </div>
        </li>
      ))}
    </ul>
  );
}
